// ─── Collection Engine ────────────────────────────────────────────────────────
//
// Phase 3 typed wrapper around dataEngine's per-collection API.
// Adds app: event emission on every mutation (insert/update/delete).
// All CRUD routes through here; callers do not import dataEngine directly.

import { dataEngine } from '@/appEngine/dataEngine';
import { eventBus } from '@/events/eventBus';
import { modelRegistry } from './modelRegistry';
import type {
  CollectionRecord,
  CollectionQuery,
  MutationResult,
} from '@/appEngine/dataEngine';
import type { SchemaValue } from '@/core/schema/nuvraSchema';

export type { CollectionRecord, CollectionQuery, MutationResult };

function projectId(): string {
  return modelRegistry.getProjectId() ?? 'unknown';
}

function missing(collection: string): MutationResult {
  return {
    success: false,
    errors: [{ field: '_collection', message: `Collection "${collection}" not found`, rule: 'exists' }],
  };
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function insertRecord(
  collection: string,
  data: Record<string, SchemaValue>,
): MutationResult {
  const col = dataEngine.collection(collection);
  if (!col) return missing(collection);

  const result = col.insert(data);
  if (result.success && result.record) {
    eventBus.emit({
      type: 'app:record_inserted',
      projectId: projectId(),
      collection,
      recordId: result.record._id,
    });
  }
  return result;
}

export function updateRecord(
  collection: string,
  id: string,
  data: Partial<Record<string, SchemaValue>>,
): MutationResult {
  const col = dataEngine.collection(collection);
  if (!col) return missing(collection);

  const result = col.update(id, data);
  if (result.success && result.record) {
    eventBus.emit({
      type: 'app:record_updated',
      projectId: projectId(),
      collection,
      recordId: result.record._id,
    });
  }
  return result;
}

export function deleteRecord(collection: string, id: string): boolean {
  const col = dataEngine.collection(collection);
  if (!col) return false;

  const record = col.getById(id);
  const deleted = col.delete(id);
  if (deleted && record) {
    eventBus.emit({
      type: 'app:record_deleted',
      projectId: projectId(),
      collection,
      recordId: record._id,
    });
  }
  return deleted;
}

// ─── Reads ────────────────────────────────────────────────────────────────────

export function queryCollection(
  collection: string,
  query: CollectionQuery = {},
): CollectionRecord[] {
  return dataEngine.collection(collection)?.query(query) ?? [];
}

export function getRecord(collection: string, id: string): CollectionRecord | null {
  return dataEngine.collection(collection)?.getById(id) ?? null;
}

export function countRecords(collection: string, query?: CollectionQuery): number {
  return dataEngine.collection(collection)?.count(query) ?? 0;
}

export function getAllRecords(collection: string): CollectionRecord[] {
  return dataEngine.collection(collection)?.all() ?? [];
}

// ─── Reactive ─────────────────────────────────────────────────────────────────

export function subscribeToCollection(
  collection: string,
  listener: (event: 'insert' | 'update' | 'delete', record: CollectionRecord) => void,
): () => void {
  return dataEngine.collection(collection)?.onChange(listener) ?? (() => {});
}
